/**
 * Выгрузка копий на Яндекс.Диск.
 *
 * Диск говорит на WebDAV — это обычный HTTP: PUT кладёт файл, PROPFIND читает
 * список, DELETE убирает лишнее. Пароль приложения вместо OAuth: не нужно ни
 * регистрировать приложение, ни проходить проверку, ни обновлять токены, а
 * человек может отозвать доступ в любой момент, не меняя пароль от аккаунта.
 *
 * ## Почему только в приложении на телефоне
 *
 * Проверено запросом: на предполётный `OPTIONS` Диск отвечает 401 и не шлёт ни
 * одного заголовка `Access-Control-Allow-*`. Значит, из вкладки браузера
 * запрос заблокирует сам браузер — не по нашей воле и не поправимо с нашей
 * стороны. В приложении запрос уходит мимо WebView, нативным HTTP, и правило
 * происхождения к нему не применяется.
 *
 * Честность здесь важнее универсальности: обещать выгрузку там, где она
 * невозможна, — это оставить человека без копии, уверенным в обратном.
 *
 * ## Что уходит на Диск
 *
 * Тот же файл, что ложится в папку на телефоне. В нём есть записи о здоровье и
 * лекарствах, поэтому выгрузка не включается сама: логин и пароль приложения
 * человек вводит руками, и он же может её выключить.
 */

import { CapacitorHttp } from '@capacitor/core';

import type { IsoDate } from '@/shared/lib/datetime';

import { planRetention } from './retention';

/** Адрес WebDAV Яндекс.Диска. */
export const DISK_BASE_URL = 'https://webdav.yandex.ru';

/** Папка на Диске — та же, что и на телефоне. */
export const DISK_FOLDER = 'Kairo';

export interface DiskCredentials {
  login: string;
  /** Пароль приложения, а не от аккаунта: его можно отозвать отдельно. */
  password: string;
}

export interface DiskFile {
  name: string;
  date: IsoDate;
  size: number;
}

/** Чем закончилась попытка достучаться до Диска. */
export type DiskCheck = 'ok' | 'auth' | 'unavailable' | 'network';

export interface DavResponse {
  status: number;
  body: string;
}

/**
 * Запрос к Диску.
 *
 * Отдельный тип, а не прямой вызов плагина: так вся логика — коды ответов,
 * разбор списка, прореживание — проверяется без сети и без телефона.
 */
export type DavRequest = (options: {
  method: string;
  path: string;
  headers?: Record<string, string>;
  body?: string;
}) => Promise<DavResponse>;

/** Работает ли выгрузка на этом устройстве. */
export function isDiskUploadSupported(): boolean {
  // Проверяется наличие нативного HTTP, а не платформа: во вкладке браузера
  // запрос всё равно упрётся в правило происхождения.
  return typeof (CapacitorHttp as { request?: unknown } | undefined)?.request === 'function';
}

function basicAuth({ login, password }: DiskCredentials): string {
  // Через кодировку UTF-8, а не напрямую: `btoa` не принимает кириллицу, а
  // пароли и логины с ней встречаются.
  const bytes = new TextEncoder().encode(`${login}:${password}`);
  return `Basic ${btoa(String.fromCharCode(...bytes))}`;
}

/** Запрос через нативный HTTP — в обход правила происхождения. */
export function davRequest(credentials: DiskCredentials): DavRequest {
  return async ({ method, path, headers = {}, body }) => {
    const response = await CapacitorHttp.request({
      url: `${DISK_BASE_URL}${path}`,
      method,
      headers: { Authorization: basicAuth(credentials), ...headers },
      data: body,
      connectTimeout: 20_000,
      readTimeout: 60_000,
    });

    return {
      status: response.status,
      body: typeof response.data === 'string' ? response.data : '',
    };
  };
}

/**
 * Проверяет доступ к Диску.
 *
 * Разделять «неверный пароль» и «нет сети» обязательно: в первом случае
 * человеку надо исправить пароль, во втором — просто подождать, и путать эти
 * два совета значит гонять его по кругу.
 */
export async function checkDiskAccess(request: DavRequest): Promise<DiskCheck> {
  try {
    const { status } = await request({
      method: 'PROPFIND',
      path: '/',
      headers: { Depth: '0' },
    });

    if (status === 401 || status === 403) return 'auth';
    if (status >= 500) return 'unavailable';
    return status < 400 ? 'ok' : 'unavailable';
  } catch {
    return 'network';
  }
}

/**
 * Кладёт копию на Диск.
 *
 * Папка создаётся заранее: без неё Диск отвечает 409 на первую же выгрузку.
 * Повторный `MKCOL` над существующей папкой — не ошибка, а обычное дело, и
 * его ответ намеренно не проверяется.
 */
export async function uploadBackupToDisk(
  request: DavRequest,
  name: string,
  content: string,
): Promise<void> {
  try {
    await request({ method: 'MKCOL', path: `/${DISK_FOLDER}` });
  } catch {
    // Папка уже есть или её создание не удалось — выгрузка скажет об этом сама.
  }

  const { status } = await request({
    method: 'PUT',
    path: `/${DISK_FOLDER}/${name}`,
    headers: { 'Content-Type': 'application/json' },
    body: content,
  });

  if (status >= 400) throw new Error(`Диск ответил ${status}`);
}

/**
 * Забирает копию с Диска.
 *
 * Путь к переносу данных, которому не нужен ни выбор файла, ни доступ к
 * хранилищу телефона: копия из браузера уходит на Диск сама, а приложение
 * забирает её оттуда. В приложении это оказалось важнее, чем задумывалось:
 * системный выбор файла во встроенном браузере открывается не всегда.
 */
export async function downloadDiskBackup(request: DavRequest, name: string): Promise<string> {
  const { status, body } = await request({ method: 'GET', path: `/${DISK_FOLDER}/${name}` });

  if (status === 404) throw new Error('Копия не найдена на Диске');
  if (status >= 400) throw new Error(`Диск ответил ${status}`);

  return body;
}

const NAME_PATTERN = /^kairo-backup-(\d{4}-\d{2}-\d{2})\.json$/;

/** Копии, лежащие на Диске, — от свежей к старой. */
export async function listDiskBackups(request: DavRequest): Promise<DiskFile[]> {
  const { status, body } = await request({
    method: 'PROPFIND',
    path: `/${DISK_FOLDER}`,
    headers: { Depth: '1' },
  });

  // Папки ещё нет — значит, и копий нет. Это обычное состояние до первой
  // выгрузки, а не сбой.
  if (status === 404) return [];
  if (status >= 400) throw new Error(`Диск ответил ${status}`);

  return parseDavList(body);
}

/**
 * Разбирает ответ WebDAV.
 *
 * Пространство имён спрашивается явно: приставку `d:`, `D:` или вовсе никакой
 * сервер выбирает сам, и разбор по имени тега ломался бы от смены сервера.
 */
export function parseDavList(xml: string): DiskFile[] {
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  const files: DiskFile[] = [];

  for (const entry of document.getElementsByTagNameNS('DAV:', 'response')) {
    const href = entry.getElementsByTagNameNS('DAV:', 'href')[0]?.textContent ?? '';
    const name = decodeURIComponent(href.split('/').filter(Boolean).pop() ?? '');

    const date = NAME_PATTERN.exec(name)?.[1];
    if (date === undefined) continue;

    const length = entry.getElementsByTagNameNS('DAV:', 'getcontentlength')[0]?.textContent;
    files.push({ name, date, size: Number(length ?? 0) });
  }

  return files.sort((a, b) => b.date.localeCompare(a.date));
}

/**
 * Убирает с Диска лишние копии — по тому же правилу, что и на телефоне.
 *
 * Возвращает, сколько файлов удалено. Без прореживания Диск за три года
 * заполнялся бы тысячей файлов, и человек платил бы за место, которое ему не
 * нужно.
 */
export async function pruneDiskBackups(request: DavRequest): Promise<number> {
  const { drop } = planRetention(await listDiskBackups(request));

  let removed = 0;
  for (const file of drop) {
    try {
      await request({ method: 'DELETE', path: `/${DISK_FOLDER}/${file.name}` });
      removed += 1;
    } catch {
      // Файл могли убрать с Диска вручную — цель достигнута и без нас.
    }
  }

  return removed;
}
